<template>
  <span
    class="term__wrapper"
    v-focusable="{ attachTabIndex: false }"
    tabindex="-1"
    @focus.capture="onMouseEnter"
    @blur.capture="onMouseLeave"
  >
    <span
      :class="TERM_TITLE_CONTAINER_CLASS"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      ref="termTitle"
    >
      <a-render-html
        v-if="
          $_hydrationHelpers_isLayoutMobileOrTablet &&
            $helper.isNotAmpPage($route.name)
        "
        :html="content"
        :container-tag="$options.consts.HTML_TAG.SPAN"
        class="term__term-title"
        @click="showPopup"
      />
      <a-link
        v-else
        :to="termRoute"
        open-in-new-tab
        class="term__term-title ignore-html-styles"
      >
        <a-render-html
          :html="content"
          :container-tag="$options.consts.HTML_TAG.SPAN"
        />
      </a-link>
    </span>
    <!-- Fixes a DOM mismatch issue (but the original cause remains unclear ) -->
    <client-only>
      <fade-transition v-if="$helper.isNotAmpPage($route.name)">
        <span
          v-show="isPopupVisible"
          class="term__pop-up-wrapper display-block"
        >
          <span
            class="display-block"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
          >
            <a-link
              v-on-clickaway="onTermLinkClickaway"
              class="term__pop-up ignore-html-styles"
              :style="popupStyle"
              :to="termRoute"
              open-in-new-tab
            >
              <span class="term__pop-up-content ignore-html-styles">
                <span
                  class="text-body term__header bold display-block ignore-html-styles"
                >
                  {{ name }}
                </span>
                <a-dots
                  :key="renderKey"
                  :text="descriptionText"
                  :row-count="2"
                  :row-height="16"
                  class="term__description"
                />
                <span class="text-caption-legacy term__link ignore-html-styles"
                  >Learn more
                </span>
                <span
                  class="term__close-button"
                  @click.prevent.stop="closePopup"
                  @touchend.stop
                >
                  <a-icon
                    class="term__close-icon"
                    :width="16"
                    :height="16"
                    :icon="$options.consts.ICON.CROSS"
                  />
                </span>
              </span>
            </a-link>
            <i class="term__beak-icon" />
          </span>
        </span>
      </fade-transition>
    </client-only>
  </span>
</template>

<script>
import { pipe } from 'ramda'

import AIcon, { ICON } from 'shared/AIcon/index.vue'
import { REFS } from '@/utils/enums/external-refs'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import mixins from '@/utils/mixins'
import ADots from 'shared/ADots'
import { directive as onClickaway } from 'vue-clickaway'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { processHtml } from '@/utils/helpers/processHtml'
import ARenderHtml from 'shared/ARenderHtml/index.vue'
import { HTML_TAG } from '@fmpedia/enums'
import { getParentWith } from '@fmpedia/helpers'

const TERM_TITLE_CONTAINER_CLASS = 'term__term-title-container'

/**
 * Be aware that we replaced all DIV tags with SPAN to avoid using div inside p
 * Where: ATerm component is used in article body.
 * If div is rendered inside p, render errors/warnings occur.
 * Bad template: <p>Hello<div>test</div></p>
 */

const POPUP_SETTINGS = {
  MARGIN: 24,
  WIDTH: 256,
  CLOSE_TIMEOUT: 200
}

const Z_INDEX = {
  VISIBLE: 1000,
  HIDDEN: 1
}

const DESCRIPTION_MAX_LENGTH = 500

export default {
  name: 'ATerm',
  mixins: [mixins.urlGenerators, hydrationHelpers],
  components: { ADots, ARenderHtml, AIcon },
  directives: { onClickaway },
  props: {
    content: propValidator([PROP_TYPES.STRING]),
    name: propValidator([PROP_TYPES.STRING]),
    description: propValidator([PROP_TYPES.STRING]),
    slug: propValidator([PROP_TYPES.STRING])
  },
  consts: { REFS, HTML_TAG, ICON },
  data() {
    return {
      isPopupVisible: false,
      timeoutId: null,
      renderKey: this.$helper.guid(),
      popupStyle: {
        width: 0,
        left: 0,
        zIndex: this.$_hydrationHelpers_mobileOrDesktopValue(
          Z_INDEX.VISIBLE,
          Z_INDEX.HIDDEN
        )
      },
      TERM_TITLE_CONTAINER_CLASS
    }
  },
  watch: {
    $_hydrationHelpers_windowWidth: {
      immediate: true,
      handler(newVal) {
        if (newVal && process.client) {
          this.$nextTick(this.setPopupStyle)
        }
      }
    }
  },
  computed: {
    termRoute() {
      return this.generateTermRoute({ Slug: this.slug || '' })
    },
    descriptionText() {
      const text = pipe(processHtml, this.$helper.htmlToText)(this.description)

      return text.slice(0, DESCRIPTION_MAX_LENGTH)
    }
  },
  methods: {
    onTermLinkClickaway(e) {
      if (!this.isPopupVisible) return

      const termContainerParent = getParentWith(e.target, {
        className: TERM_TITLE_CONTAINER_CLASS
      })

      const isSameTerm = termContainerParent === this.$refs.termTitle

      if (isSameTerm) return

      this.closePopup()
    },
    onMouseEnter() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      this.popupStyle.zIndex = Z_INDEX.VISIBLE

      this.showPopup()
    },
    onMouseLeave() {
      if (this.$_hydrationHelpers_isLayoutMobileOrTablet) return

      this.popupStyle.zIndex = Z_INDEX.HIDDEN

      this.timeoutId = setTimeout(this.closePopup, POPUP_SETTINGS.CLOSE_TIMEOUT)
    },
    showPopup() {
      this.isPopupVisible = true
      this.$nextTick(() => {
        this.renderKey = this.$helper.guid()
      })
      clearTimeout(this.timeoutId)
    },
    closePopup() {
      if (!this.isPopupVisible) return

      this.isPopupVisible = false
    },
    setPopupStyle() {
      this.popupStyle = this.calculatePopupStyle()
    },
    calculatePopupStyle() {
      /**
       * We render two article instances wrapped to AVisibility. One of them is
       * hidden and refs are not accessible.
       */

      if (!this.$refs.termTitle) return

      const termPosition = this.$refs.termTitle.getBoundingClientRect()
      const termCenterX = termPosition.left + termPosition.width / 2

      const offsetLeft =
        POPUP_SETTINGS.WIDTH / 2 - termPosition.left + POPUP_SETTINGS.MARGIN
      const offsetRight =
        termCenterX +
        POPUP_SETTINGS.WIDTH / 2 +
        POPUP_SETTINGS.MARGIN -
        window.innerWidth

      const positionLeft =
        POPUP_SETTINGS.WIDTH / 2 > termPosition.left
          ? `${offsetLeft}px`
          : POPUP_SETTINGS.WIDTH / 2 > window.innerWidth - termPosition.right
          ? `calc(50% - ${offsetRight}px)`
          : `50%`

      return {
        width: `${POPUP_SETTINGS.WIDTH}px`,
        left: positionLeft,
        zIndex: Z_INDEX.VISIBLE
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.term__wrapper {
  position: relative;
  display: inline-block;

  .term__term-title-container {
    display: inline-block;
  }

  a.term__term-title,
  span.term__term-title {
    display: inline-block;
    padding: 2px 4px;
    color: $c--black;
    background: rgba($c--main, 0.25);
    border-radius: 2px;
    white-space: nowrap;
  }

  .term__pop-up {
    position: absolute;
    top: -12px;
    transform: translate(-50%, -100%);
    display: flex;
    gap: 8px;
    padding: 8px 36px 8px 8px;
    border-radius: 4px;
    border: 2px solid $c--main;
    background: $c--gray-soft;

    &-content {
      display: flex;
      flex-direction: column;
      padding: 4px 8px;
      gap: 8px;

      .term__header {
        position: relative;
        line-height: 22px;
      }

      .term__description {
        position: relative;
        font-size: 14px;
        line-height: 16px;
      }

      .term__link {
        color: $c--main;
        font-weight: 400;
        line-height: 16px;
        text-decoration: underline;
        text-underline-offset: auto;
        text-underline-position: from-font;
      }
    }
  }
}
.term__close-button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 20px;
  height: 20px;
  top: 8px;
  right: 8px;
}

.term__close-icon {
  fill: $c--main;
}

.term__beak-icon {
  position: absolute;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid $c--main;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
}
</style>
